import React, { useContext, useState, useMemo } from "react";
import createMailCell from "../../../tablecells/Mailbox";
import { Table, TableHead, TableBody, TableRow, TableCell, Paper, MenuItem, Select, FormControl, InputLabel, IconButton, TableSortLabel } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Typography, Box } from "@mui/material";
import { ThemeContext } from "../../../../context/ThemeContext";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { SendToDepartment, SendToUsername, deleteMail } from "../../../../api/request/dashboard/dashboard";
import toast from "react-hot-toast";
import DeleteMail from "./DeleteMail";
import ViewMail from "./ViewMail";
import { useTranslation } from "react-i18next";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { TableVirtuoso } from "react-virtuoso";
import { deleteEmail } from '../../../../store/Slices/emailsSlice'
import NoDataTable from "../../../table/NoDataTable";

export default function Mail() {
  const [open, setOpen] = useState(false);
  const { darkMode } = useContext(ThemeContext);
  const [content, setContent] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [mailToDelete, setMailToDelete] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedRecipientType, setSelectedRecipientType] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [subject, setSubject] = useState("");
  const [viewOpen, setViewOpen] = useState(false);
  const [mailToView, setMailToView] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { t } = useTranslation()
  const { emails = [], users = [], departments = [] } = useSelector(state => state.email, shallowEqual);
  const dispatch = useDispatch();
  const imgSrc = darkMode ? '/hs-white.png' : '/hs-logo.png'
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  console.log(emails);

  const handleOpen = () => { setOpen(true); };

  const handleClose = () => {
    setSelectedDepartment("");
    setSelectedRecipientType('');
    setContent("");
    setSubject('');
    setOpen(false);
  };


  const handleChange = (value) => {
    setContent(value);
    console.log("Content updated:", value);
  };

  const quillModules = {
    toolbar: [
      [{ size: [] }], ["bold", "italic", "underline", "strike"], [{ list: "ordered" }, { list: "bullet" }],
      ["blockquote", "code-block"], ["link", "image"], [{ align: [] }], ["clean"],
    ],
  };

  const quillFormats = ["font", "size", "bold", "italic", "underline", "strike", "list", "bullet", "blockquote", "code-block", "link", "image", "align",];

  const handleDeleteOpen = (mail) => {
    setMailToDelete(mail);
    setDeleteOpen(true);
  };

  const handleViewOpen = (mail) => {
    setMailToView(mail);
    setViewOpen(true);
  }

  const handleDelete = async () => {
    if (!mailToDelete) {
      console.error("No mail selected for deletion.");
      return;
    }

    const data = { id: mailToDelete.id };

    try {
      const response = await deleteMail(data);
      console.log("Response:", response);

      if (response.status === 200) {
        dispatch(deleteEmail(mailToDelete.id));
      }
    } catch (error) {
      console.error("Error deleting mail:", error);
      toast.error("Failed to delete mail.");
    }
  };


  const handleRecipientTypeChange = (event) => {
    setSelectedRecipientType(event.target.value);
  };

  const handleDepartmentChange = (event) => {
    setSelectedDepartment(event.target.value);
    console.log("Selected department:", event.target.value);
  };

  const columns = createMailCell({ handleOpen, handleDeleteOpen, handleViewOpen, t });
  const selectedUserDetails = users.find(user => user.id === selectedUser);

  const handleSendMail = async () => {

    if (!subject || !content || (!selectedDepartment && !selectedUser)) {
      toast.error("Please fill in all fields.");
      return;
    }

    try {
      const data = {
        content: content,
        departmentId: selectedDepartment,
        subject: subject,
      };


      const dataForuser = {
        content: content,
        subject,
        toUserType: selectedUserDetails ? selectedUserDetails.senderType : null,
        toUsername: selectedUser,
      }
      const response = await (selectedRecipientType === "department"
        ? SendToDepartment(data)
        : SendToUsername(dataForuser));

      if (response.status === 200) {
        toast.success("Mail sent successfully!");
      } else {
        toast.error("Failed to send mail.");
      }

      handleClose();
    } catch (error) {
      console.error("Error sending mail:", error);
      // toast.error("Error sending mail.");
    }
  };

  const filteredEmails = useMemo(() => {
    let sorted = [...emails].filter(
      (email) =>
        email.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        email.senderName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (sortConfig.key) {
      sorted.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return sorted;
  }, [emails, searchQuery, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const Row = useMemo(() => (index, email) => {
    return (
      <>
        {columns.map((col, colIndex) => (
          <TableCell key={colIndex} sx={{ fontSize: "0.8rem", border: "none", padding: "5px 5px", backgroundColor: darkMode ? "#151924" : "#fff", position: "relative", overflow: "hidden", }}>
            {col.field === "senderName" ? email?.senderUsername
              : col.field === "subject" ? email?.subject
                : col.field === "dateTime" ? new Date(email?.dateTime).toLocaleString()
                  : col.field === "unread" ? email?.unread ? `${t("unread")}` : `${t("read")}`
                    : col.field === "actions" ? col.render(email)
                      : email[col.field] || "-"}
          </TableCell>
        ))}
      </>
    );
  }, [columns, darkMode, t]);

  const fixedHeaderContent = () => {
    return (
      <TableRow sx={{ borderTop: `1px solid ${darkMode ? "var(--borderDark)" : "var(--borderLight)"}` }}>
        {columns.map((col, index) => (
          <TableCell onClick={() => col.field !== t('actions') && handleSort(col.field)} key={index} sx={{ fontSize: "0.85rem", fontWeight: 550, textTransform: "capitalize", p: 1, backgroundColor: darkMode ? "var(--background-color)" : "var(--white)", borderBottom: "1px solid rgba(224, 224, 224, 0.3)", borderRight: `1px solid ${darkMode ? "#444" : "var(--borderLight)"}`, cursor: col.field !== t('actions') ? "pointer" : "default", }}>
            <TableSortLabel active={sortConfig.key === col.field} direction={sortConfig.direction }>{col.title}</TableSortLabel>
          </TableCell>
        ))}
      </TableRow>);
  }

  const VirtuosoTableComponents = {
    Table: (props) => <Table {...props} sx={{ borderCollapse: "separate", tableLayout: "fixed" }} />,
    TableHead: React.forwardRef((props, ref) => <TableHead {...props} ref={ref} />),
    TableBody: React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
  };

  return (
    <>

      <Paper style={{ width: "100%", height: "calc(100% - 35px)", position: 'relative', backgroundColor: darkMode ? "#151924" : "#fff", }}>
        {filteredEmails.length === 0 ? (<NoDataTable columns={columns} data={filteredEmails} noDataMessage={t('noData')} />
        ) : (
          <>
            <Box sx={{ backgroundColor: darkMode ? "#151924" : "#fff", height: '100%' }}>
              <TableVirtuoso data={filteredEmails} fixedHeaderContent={fixedHeaderContent} itemContent={Row} components={VirtuosoTableComponents} />
            </Box>
            <div style={{ height: 35 }}>
              <div className='flex items-center px-1 justify-between'>
                <TextField size="small" variant="outlined" placeholder={t("mobSearch")} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} sx={{ width: '250px', mx: 1, mt: 0.3 }} InputProps={{ sx: { fontSize: 13, padding: 0, height: 30, "& input": { padding: "0", paddingLeft: 1, height: "30px", }, }, }} />
                <div className='flex items-center opacity-50'>
                  <small>{t('poweredby')} </small><a href="https://hybridsolutions.com/" target='_blank'><img src={imgSrc} alt="hs" style={{ height: darkMode ? 40 : 20 }} className='ms-2' />
                  </a>
                </div>
              </div>
            </div>
          </>
        )}
      </Paper>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <Box>
          <DialogTitle sx={{ color: "white", p: 1 }} className="bg-primary">
            <Typography id="symbol-details-modal" variant="body1" sx={{ color: darkMode ? "var(--white)" : "var(--white)" }} align="center">{t('newEmail')}</Typography>
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: darkMode ? "#151924" : "#f5f5f5", height: "400px", display: 'flex', gap: 2, flexDirection: 'column' }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 2, mt: 3 }}>

              <FormControl fullWidth>
                <InputLabel>{t('enterrecipient')}</InputLabel>
                <Select label={t('enterrecipient')} value={selectedRecipientType} onChange={handleRecipientTypeChange} fullWidth variant="outlined" sx={{ height: 45 }}>
                  <MenuItem value="department">{t('department')}</MenuItem>
                  <MenuItem value="client">{t('client')}</MenuItem>
                </Select>
              </FormControl>

              {selectedRecipientType === "department" && (
                <FormControl fullWidth>
                  <InputLabel>{t('selectDepartment')}</InputLabel>
                  <Select label={t('selectDepartment')} value={selectedDepartment} onChange={handleDepartmentChange} fullWidth variant="outlined" sx={{ height: 45 }}>
                    {departments.map((department, index) => (
                      <MenuItem key={index} value={department.id}>{department.description}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {selectedRecipientType === "user" && (
                <FormControl fullWidth>
                  <InputLabel>{t('selectClient')}</InputLabel>
                  <Select label={t('selectClient')} value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)} fullWidth variant="outlined" sx={{ height: 45 }}>
                    {users.map((user, index) => (
                      <MenuItem key={index} value={user.id}>{user.description}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <TextField label={t('entersubject')} placeholder={t('entersubject')} variant="outlined" fullWidth value={subject} onChange={(e) => setSubject(e.target.value)} sx={{ "& .MuiOutlinedInput-root": { height: 45 } }} />
              <ReactQuill value={content} onChange={handleChange} theme="snow" placeholder={t('writeyourmessage')} modules={quillModules} formats={quillFormats} style={{ height: "200px", marginBottom: "2px", color: darkMode ? "#fff" : "#000", border: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}` }} />
            </Box>

          </DialogContent>
          <DialogActions sx={{ padding: "16px", backgroundColor: darkMode ? "#151924" : "#f5f5f5" }}>
            <Box sx={{ display: "flex", justifyContent: "end" }}>
              <Button onClick={handleClose} variant="outlined" color="primary" sx={{ marginRight: 2 }}>{t('cancel')}</Button>
              <Button onClick={handleSendMail} variant="contained" color="primary">{t('send')}</Button>
            </Box>
          </DialogActions>
        </Box>
      </Dialog>
      <DeleteMail open={deleteOpen} handleClose={() => setDeleteOpen(false)} handleDelete={handleDelete} />
      {/* handleDelete={handleDelete} */}
      <ViewMail open={viewOpen} handleClose={() => setViewOpen(false)} mail={mailToView} />

    </>
  );
}
